import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { StatutCommande, Commande, statutLabels, statutColors } from '../../mocks/commandes';
import { produitsMock, utilisateursMock } from '../../mocks/stocks';
import { api } from '../../services/api';
import CommandeTimeline from './components/CommandeTimeline';
import TissuAssignModal from './components/TissuAssignModal';
import ModeleSelectModal from './components/ModeleSelectModal';
import { AssignmentModal } from './components/AssignmentModal';
import FactureModal from '../../components/feature/FactureModal';
import { EntrepriseSettings, entrepriseSettingsMock } from '../../mocks/entreprise';
import { checkPermission, PERMISSIONS } from '../../utils/permissions';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { formatPrice } from '../../utils/format';
import { useBranding } from '../../context/BrandingContext';

interface UserInfo { id: string; nom: string; role: string; can_edit_commandes: number; }

const STATUS_ORDER: StatutCommande[] = ['EN_ATTENTE', 'EN_COUPE', 'EN_COUTURE', 'TERMINE', 'LIVRE'];

const nextStepLabel: Partial<Record<StatutCommande, string>> = {
  EN_ATTENTE: 'Lancer la coupe',
  EN_COUPE: 'Valider la coupe',
  EN_COUTURE: 'Valider la couture',
  TERMINE: 'Marquer livré',
};

const nextStepIcon: Partial<Record<StatutCommande, string>> = {
  EN_ATTENTE: 'ri-scissors-line',
  EN_COUPE: 'ri-checkbox-circle-line',
  EN_COUTURE: 'ri-checkbox-circle-line',
  TERMINE: 'ri-truck-line',
};


const getArticleNomHelper = (aff: any, articles: any[], fallbackModele?: string) => {
  if (aff.article_nom) return aff.article_nom;
  if (aff.article_id && articles) {
    const art = articles.find((a: any) => Number(a.id) === Number(aff.article_id));
    if (art) return `${art.type_habit_nom || 'Article'} (${art.modele_nom || 'Sur mesure'})`;
  }
  return articles && articles.length > 0
    ? articles.map((a: any) => a.type_habit_nom || 'Article').join(', ')
    : (fallbackModele || 'Toute la commande');
};


const renderDetailedSubTasks = (cmd: any, type: string) => {
  const affs = (cmd.affectations || []).filter((a: any) => a.type === type);
  if (affs.length === 0) return null;

  const groups: Record<string, { tache: string; articleNom: string; finished: number; total: number; personnels: string[] }> = {};
  affs.forEach((aff: any) => {
    const taskText = aff.tache || 'Tâche';
    const key = `${aff.article_id || 'global'}_${taskText}`;
    if (!groups[key]) {
      groups[key] = {
        tache: taskText,
        articleNom: aff.article_name || aff.article_nom ? ` (${aff.article_name || aff.article_nom})` : '',
        finished: 0,
        total: 0,
        personnels: []
      };
    }
    if (aff.statut === 'TERMINE') {
      groups[key].finished += Number(aff.quantite || 1);
    }
    groups[key].total += Number(aff.quantite || 1);
    if (aff.personnel_nom && !groups[key].personnels.includes(aff.personnel_nom)) {
      groups[key].personnels.push(aff.personnel_nom);
    }
  });

  const getIcon = (tache: string) => {
    if (!tache) return '📋';
    const lower = tache.toLowerCase();
    if (lower.includes('haut') || lower.includes('habit') || lower.includes('robe') || lower.includes('chemise') || lower.includes('boubou') || lower.includes('veste')) return '👗';
    if (lower.includes('pantalon') || lower.includes('bas') || lower.includes('culotte') || lower.includes('jupe')) return '👖';
    if (lower.includes('coupe')) return '✂️';
    if (lower.includes('couture') || lower.includes('complet')) return '🧵';
    if (lower.includes('broderie') || lower.includes('motif')) return '✨';
    return '📋';
  };

  return (
    <div className="mt-2 space-y-1.5 pl-1 w-full">
      {Object.values(groups).map((g, idx) => (
        <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between text-[11px] bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1 gap-1 w-full shadow-sm">
          <span className="font-bold text-gray-700 flex items-center gap-1.5">
            <span>{getIcon(g.tache)}</span>
            <span>{g.tache}{g.articleNom}</span>
          </span>
          <div className="flex items-center gap-2 font-semibold text-gray-500">
            <span className={g.finished === g.total ? 'text-green-600 font-bold' : 'text-gray-700'}>
              {g.finished} / {g.total}
            </span>
            <span className="text-[10px] text-gray-400 italic">
              (par {g.personnels.join(', ')})
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};


export default function CommandeDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [commande, setCommande] = useState<Commande | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showTissuModal, setShowTissuModal] = useState(false);
  const [showValidModal, setShowValidModal] = useState(false);
  const [validComment, setValidComment] = useState('');
  const [showAssignModal, setShowAssignModal] = useState<'COUPE' | 'COUTURE' | 'BRODERIE' | null>(null);
  const [validations, setValidations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAffIds, setSelectedAffIds] = useState<number[]>([]);
  const [completedQuantities, setCompletedQuantities] = useState<Record<number, number>>({});
  const [singleAffToValidate, setSingleAffToValidate] = useState<any | null>(null);
  const [singleAffToStart, setSingleAffToStart] = useState<any | null>(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [startQuantity, setStartQuantity] = useState(1);
  const [assignmentToEdit, setAssignmentToEdit] = useState<any | null>(null);

  useEffect(() => {
    if (showStartModal && singleAffToStart) {
      setStartQuantity(Number(singleAffToStart.quantite || 1));
    } else if (!showStartModal) {
      setSingleAffToStart(null);
      setStartQuantity(1);
    }
  }, [showStartModal, singleAffToStart]);

  const getActiveWorkshopType = () => {
    if (!commande) return 'COUPE';
    return commande.statut === 'EN_COUTURE' ? 'COUTURE' : 'COUPE';
  };

  useEffect(() => {
    if (showValidModal && commande) {
      if (!singleAffToValidate) {
        const activeType = getActiveWorkshopType();
        const activeAffs = (commande.affectations || []).filter(
          (a: any) => (a.type === activeType || (activeType === 'COUPE' && a.type === 'BRODERIE')) && a.statut !== 'TERMINE'
        );
        setSelectedAffIds(activeAffs.map((a: any) => Number(a.id)));
      }
      setCompletedQuantities({});
    } else {
      setSelectedAffIds([]);
      setCompletedQuantities({});
      setSingleAffToValidate(null);
    }
  }, [showValidModal, commande, singleAffToValidate]);

  // Helper for dynamic action button
  const getSmartAction = () => {
    if (!commande) return { label: 'Chargement...', icon: 'ri-loader-line', nextStatut: commande?.statut, color: 'bg-gray-400', disabled: true };
    
    const affs = (commande as any).affectations || [];
    const currentStatut = commande.statut;

    // Analyse des affectations par type
    const coupeAffs = affs.filter((a: any) => a.type === 'COUPE');
    const coutureAffs = affs.filter((a: any) => a.type === 'COUTURE');
    const broderieAffs = affs.filter((a: any) => a.type === 'BRODERIE');

    const allFinished = (list: any[]) => list.length > 0 && list.every(a => a.statut === 'TERMINE');
    const someInProgress = (list: any[]) => list.some(a => a.statut === 'EN_COURS' || a.statut === 'TERMINE');
    const noneStarted = (list: any[]) => list.length > 0 && list.every(a => a.statut === 'EN_ATTENTE');

    // 1. Logique EN_ATTENTE
    if (currentStatut === 'EN_ATTENTE') {
      if (allFinished(coupeAffs)) {
        return { label: 'Valider la coupe', icon: 'ri-checkbox-circle-line', nextStatut: 'EN_COUPE', color: 'bg-foret-600' };
      }
      if (someInProgress(coupeAffs)) {
        return { label: 'Lancer officiellement la coupe', icon: 'ri-scissors-line', nextStatut: 'EN_COUPE', color: 'bg-blue-500' };
      }
      if (coupeAffs.length === 0) {
        return { label: 'Assigner des coupeurs', icon: 'ri-user-add-line', nextStatut: 'EN_ATTENTE', onClick: () => setShowAssignModal('COUPE'), color: 'bg-amber-500' };
      }
    }

    // 2. Logique EN_COUPE
    if (currentStatut === 'EN_COUPE') {
       if (allFinished(coupeAffs)) {
          // Si on a aussi de la broderie prévue et pas encore finie
          if (broderieAffs.length > 0 && !allFinished(broderieAffs)) {
             return { label: 'Attente broderie...', icon: 'ri-time-line', nextStatut: 'EN_COUTURE', color: 'bg-amber-400', disabled: true };
          }
          return { label: 'Passer à la couture', icon: 'ri-arrow-right-line', nextStatut: 'EN_COUTURE', color: 'bg-foret-600' };
       }
       return { label: 'Coupe en cours...', icon: 'ri-loader-4-line', nextStatut: 'EN_COUPE', disabled: true, color: 'bg-gray-400' };
    }

    // 3. Logique EN_COUTURE
    if (currentStatut === 'EN_COUTURE') {
       if (allFinished(coutureAffs)) {
         return { label: 'Terminer la commande', icon: 'ri-checkbox-line', nextStatut: 'TERMINE', color: 'bg-foret-700' };
       }
       if (coutureAffs.length === 0) {
          return { label: 'Assigner des tailleurs', icon: 'ri-user-add-line', nextStatut: 'EN_COUTURE', onClick: () => setShowAssignModal('COUTURE'), color: 'bg-amber-500' };
       }
       return { label: 'Couture en cours...', icon: 'ri-loader-4-line', nextStatut: 'EN_COUTURE', disabled: true, color: 'bg-gray-400' };
    }

    const nextIdx = STATUS_ORDER.indexOf(currentStatut) + 1;
    const nextStatut = STATUS_ORDER[nextIdx] || currentStatut;

    return {
      label: nextStepLabel[currentStatut] || 'Avancer',
      icon: nextStepIcon[currentStatut] || 'ri-arrow-right-line',
      nextStatut: nextStatut,
      color: 'bg-foret-500'
    };
  };

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showModeleModal, setShowModeleModal] = useState(false);
  const measuresRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newPayment, setNewPayment] = useState({ montant: 0, mode: 'Espèces' });
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const { branding } = useBranding();
  const [showFacture, setShowFacture] = useState(false);
  const [showRemiseModal, setShowRemiseModal] = useState(false);
  const [remiseValue, setRemiseValue] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Dynamically fetched personnel state
  const [coupeurs, setCoupeurs] = useState<{ id: string; nom: string; role: string }[]>([]);
  const [tailleurs, setTailleurs] = useState<{ id: string; nom: string; role: string }[]>([]);
  const [modeles, setModeles] = useState<any[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored) setUser(JSON.parse(stored));
    else navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token || !id) return;
      try {
        const [orderData, historyData, personnelData, modelesData, paymentsData, settingsData] = await Promise.all([
          api.getCommande(token, id),
          api.getValidationHistory(token, id),
          api.getPersonnel(token).catch(() => []),
          api.getModeles(token).catch(() => []),
          api.getCommandePaiements(token, id).catch(() => []),
          api.getSettings(token).catch(() => null)
        ]);
        
        if (modelesData) setModeles(modelesData);
        if (paymentsData) setPaymentHistory(paymentsData);
        
        if (personnelData && personnelData.length > 0) {
          setCoupeurs(personnelData.filter((u: any) => u.specialite === 'coupeur' || u.specialite === 'Coupeur'));
          setTailleurs(personnelData.filter((u: any) => u.specialite === 'tailleur' || u.specialite === 'Tailleur'));
        }

        if (orderData) {
          setCommande({
            ...orderData,
            client: { nom: orderData.client_nom, telephone: orderData.client_telephone },
            modele: orderData.modele_nom || 'Sur mesure',
            modelImage: orderData.modele_image || 'https://images.unsplash.com/photo-1544441893-675973e31985?q=80&w=200&auto=format&fit=crop',
            dateLivraison: orderData.date_livraison,
            mesures: orderData.mesures || {},
            tissuClient: Boolean(orderData.tissu_client),
            tissuDescription: orderData.tissu_description,
            tissuStockId: orderData.tissu_stock_id,
            tissuImage: orderData.tissu_image,
            tissuNom: orderData.tissu_nom,
            quantiteTissu: orderData.quantite_tissu,
            articles: orderData.articles || [],
            affectations: orderData.affectations || [],
            paiement: { 
              total: orderData.total, 
              remise: orderData.remise,
              acompte: orderData.acompte, 
              reste: orderData.reste, 
              modePaiement: orderData.mode_paiement,
              vendeur: orderData.vendeur_nom,
              historique: paymentsData || []
            },
            validations: historyData.map((h: any) => ({
                etape: h.etape,
                validePar: h.valide_par_nom,
                date: new Date(h.created_at).toLocaleDateString('fr-FR'),
                heure: new Date(h.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
                commentaire: h.commentaire
            }))
          });
          setValidations(historyData);
            setEditForm({
              total: orderData.total,
              acompte: orderData.acompte,
              date_livraison: orderData.date_livraison,
              mode_paiement: orderData.mode_paiement,
              modele_id: orderData.modele_id,
              notes: orderData.notes,
              client_nom: orderData.client_nom,
              client_tel: orderData.client_telephone
            });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAvancer = async () => {
    if (!commande || !id) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    const smartAction = getSmartAction();
    const newStatut = smartAction.nextStatut;

    try {
      // 1. Valider d'abord les tâches sélectionnées
      for (const affId of selectedAffIds) {
        const qtyDone = completedQuantities[affId]; // Peut être undefined
        await api.updateAffectationStatus(token, affId, 'TERMINE', qtyDone);
      }

      // 2. Valider ensuite l'étape globale de la commande
      const activeType = getActiveWorkshopType();
      const cmdAffs = (commande.affectations || []).filter(
        (a: any) => a.type === activeType || (activeType === 'COUPE' && a.type === 'BRODERIE')
      );
      const isStepFullyCompleted = cmdAffs.every((aff: any) => {
        const isCompleted = aff.statut === 'TERMINE';
        const isChecked = selectedAffIds.includes(Number(aff.id));
        if (isCompleted) return true;
        if (!isChecked) return false;
        
        const valQty = completedQuantities[Number(aff.id)] ?? Number(aff.quantite || 1);
        return valQty === Number(aff.quantite || 1);
      });

      const isValidatingCurrentStage = !singleAffToValidate || 
        singleAffToValidate.type === activeType || 
        (activeType === 'COUPE' && singleAffToValidate.type === 'BRODERIE');

      if (isStepFullyCompleted && newStatut && isValidatingCurrentStage) {
        await api.updateCommandeStatus(token, id, newStatut, validComment);
        showToast(`Action confirmée : ${smartAction.label}`);
      } else {
        showToast(`Tâches validées avec succès.`);
      }
      
      // Refresh data
      const [orderData, historyData] = await Promise.all([
        api.getCommande(token, id),
        api.getValidationHistory(token, id)
      ]);
      
      if (orderData) {
        setCommande({
          ...orderData,
          client: { nom: orderData.client_nom, telephone: orderData.client_telephone },
          modele: orderData.modele_nom || 'Sur mesure',
          mesures: orderData.mesures || {},
          tissuClient: Boolean(orderData.tissu_client),
          tissuDescription: orderData.tissu_description,
          tissuStockId: orderData.tissu_stock_id,
          tissuImage: orderData.tissu_image,
          tissuNom: orderData.tissu_nom,
          quantiteTissu: orderData.quantite_tissu,
          articles: orderData.articles || [],
          paiement: { 
            total: orderData.total, 
            remise: orderData.remise,
            acompte: orderData.acompte, 
            reste: orderData.reste, 
            modePaiement: orderData.mode_paiement,
            vendeur: orderData.vendeur_nom
          },
          validations: historyData.map((h: any) => ({
              etape: h.etape,
              validePar: h.valide_par_nom,
              date: new Date(h.created_at).toLocaleDateString('fr-FR'),
              heure: new Date(h.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
              commentaire: h.commentaire
          }))
        });
      }
      setShowValidModal(false);
      setValidComment('');
      setCompletedQuantities({});
    } catch (err) {
      showToast("Erreur lors de la validation", "error");
    }
  };

  const handleDeleteAffectation = async (affId: number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token || !id) return;
    try {
      await api.deleteAffectation(token, affId);
      showToast("Affectation retirée.");
      const data = await api.getCommande(token, id);
      if (data) setCommande(prev => prev ? { 
        ...prev, 
        affectations: data.affectations || [], 
        paiement: { ...prev.paiement, reste: data.reste } 
      } : null);
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const handleUpdateAffectationStatus = async (affId: number, nextStatut: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token || !id) return;
    try {
      await api.updateAffectationStatus(token, affId, nextStatut);
      showToast("Tâche mise à jour.");
      const data = await api.getCommande(token, id);
      if (data) setCommande(prev => prev ? { ...prev, affectations: data.affectations || [] } : null);
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const handleStartTask = async () => {
    if (!singleAffToStart || !id) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.updateAffectationStatus(token, singleAffToStart.id, 'EN_COURS', startQuantity);
      showToast("Tâche démarrée avec succès.");
      const data = await api.getCommande(token, id);
      if (data) {
        setCommande(prev => prev ? { 
          ...prev, 
          affectations: data.affectations || [],
          paiement: { ...prev.paiement, reste: data.reste }
        } : null);
      }
      setShowStartModal(false);
      setSingleAffToStart(null);
    } catch (err: any) {
      showToast(err.message || "Erreur lors du démarrage de la tâche", 'error');
    }
  };

  const handleTissuAssign = async (produit: { id: string; nom: string }, quantite: number) => {
    if (!id) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.updateCommandeTissu(token, id, {
        tissu_stock_id: produit.id,
        tissu_description: produit.nom,
        quantite_tissu: quantite,
        tissu_client: 0
      });
      showToast(`Tissu "${produit.nom}" assigné et stock mis à jour`);
      
      // Refresh
      const orderData = await api.getCommande(token, id);
      if (orderData) {
          setCommande({
              ...commande,
              ...orderData,
              client: { nom: orderData.client_nom, telephone: orderData.client_telephone },
              modele: orderData.modele_nom || 'Sur mesure',
              tissuStockId: orderData.tissu_stock_id,
              tissuNom: orderData.tissu_nom,
              quantiteTissu: orderData.quantite_tissu
          });
      }
      setShowTissuModal(false);
    } catch (err) {
      showToast("Erreur lors de l'assignation du tissu", "error");
    }
  };

  /** Rechargement complet de la commande depuis l'API avec mapping correct */
  const reloadCommande = async (token: string) => {
    try {
      const [orderData, paymentsData] = await Promise.all([
        api.getCommande(token, id!),
        api.getCommandePaiements(token, id!).catch(() => [])
      ]);
      if (orderData) {
        const payments = Array.isArray(paymentsData) ? paymentsData : [];
        setPaymentHistory(payments);
        setCommande((prev: any) => ({
          ...prev,
          ...orderData,
          boutique_id: orderData.boutique_id ?? 1,
          client: { nom: orderData.client_nom, telephone: orderData.client_telephone },
          modele: orderData.modele_nom || 'Sur mesure',
          modelImage: orderData.modele_image || prev?.modelImage || '',
          dateLivraison: orderData.date_livraison,
          mesures: orderData.mesures || {},
          tissuClient: Boolean(orderData.tissu_client),
          tissuDescription: orderData.tissu_description,
          tissuStockId: orderData.tissu_stock_id,
          tissuImage: orderData.tissu_image,
          tissuNom: orderData.tissu_nom,
          quantiteTissu: orderData.quantite_tissu,
          articles: orderData.articles || [],
          affectations: orderData.affectations || [],
          paiement: {
            total: orderData.total,
            remise: orderData.remise,
            acompte: orderData.acompte,
            reste: orderData.reste,
            modePaiement: orderData.mode_paiement,
            vendeur: orderData.vendeur_nom,
            historique: payments
          }
        }));
      }
      return orderData;
    } catch (err) {
      console.error('Erreur rechargement commande:', err);
      return null;
    }
  };

  const handleUpdateTissuBoutique = async (tissuItemId: number, targetBoutiqueId: number) => {
    if (!id) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      setLoading(true);
      await api.updateTissuBoutique(token, tissuItemId, targetBoutiqueId);
      showToast(`Tissu transféré vers Boutique ${targetBoutiqueId} — caisse recalculée !`);
      await reloadCommande(token);
    } catch (err: any) {
      showToast(err.message || "Erreur lors de la modification de boutique", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCommandeBoutique = async (targetBoutiqueId: number) => {
    if (!id) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      setLoading(true);
      await api.updateCommandeBoutique(token, id, targetBoutiqueId);
      showToast(`Commande transférée vers Boutique ${targetBoutiqueId} — caisse recalculée !`);
      await reloadCommande(token);
    } catch (err: any) {
      showToast(err.message || "Erreur lors de la modification de boutique", "error");
    } finally {
      setLoading(false);
    }
  };

  const exportAsImage = async () => {
    if (!measuresRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(measuresRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `Mesures_${commande?.client?.nom}_${commande?.id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      showToast("Image téléchargée");
    } catch (err) {
      showToast("Erreur export image", "error");
    } finally {
      setExporting(false);
    }
  };

  const exportAsPDF = async () => {
    if (!measuresRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(measuresRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 0, 10, pdfWidth, pdfHeight);
      pdf.save(`Mesures_${commande?.client?.nom}_${commande?.id}.pdf`);
      showToast("PDF téléchargé");
    } catch (err) {
      showToast("Erreur export PDF", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleUpdate = async () => {
    if (!id || !editForm) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    
    try {
      await api.updateCommande(token, id, editForm);
      showToast("Commande mise à jour", "success");
      setShowEditModal(false);
      
      // Dynamic refresh of all data to ensure synchronization
      const [orderData, historyData] = await Promise.all([
        api.getCommande(token, id),
        api.getValidationHistory(token, id)
      ]);
      
      if (orderData) {
        setCommande({
          ...orderData,
          client: { nom: orderData.client_nom, telephone: orderData.client_telephone },
          modele: orderData.modele_nom || 'Sur mesure',
          modelImage: orderData.modele_image,
          dateLivraison: orderData.date_livraison,
          mesures: orderData.mesures || {},
          tissuClient: Boolean(orderData.tissu_client),
          tissuDescription: orderData.tissu_description,
          tissuStockId: orderData.tissu_stock_id,
          tissuImage: orderData.tissu_image,
          tissuNom: orderData.tissu_nom,
          quantiteTissu: orderData.quantite_tissu,
          articles: orderData.articles || [],
          paiement: { 
            total: orderData.total, 
            remise: orderData.remise,
            acompte: orderData.acompte, 
            reste: orderData.reste, 
            modePaiement: orderData.mode_paiement,
            vendeur: orderData.vendeur_nom
          },
          validations: historyData.map((h: any) => ({
              etape: h.etape,
              validePar: h.valide_par_nom,
              date: new Date(h.created_at).toLocaleDateString('fr-FR'),
              heure: new Date(h.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
              commentaire: h.commentaire
          }))
        });
        setValidations(historyData);
      }
    } catch (err: any) {
      showToast(err.message || "Erreur de mise à jour", "error");
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.softDeleteCommande(token, id);
      showToast("Commande mise à la corbeille");
      setTimeout(() => navigate('/dashboard/atelier'), 1000);
    } catch (err) {
      showToast("Erreur suppression", "error");
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.cancelCommande(token, id);
      showToast("Commande annulée — tous les paiements ont été supprimés");
      setShowCancelConfirm(false);
      setTimeout(() => navigate('/dashboard/atelier'), 1500);
    } catch (err) {
      showToast("Erreur lors de l'annulation", "error");
    }
  };

  const handleApplyRemise = async () => {
    if (!id || remiseValue < 0) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.updateCommande(token, id, { remise: remiseValue });
      showToast(`Remise de ${formatPrice(remiseValue)} appliquée !`);
      setShowRemiseModal(false);
      // Refresh
      const orderData = await api.getCommande(token, id);
      if (orderData && commande) {
        setCommande({
          ...commande,
          ...orderData,
          client: { nom: orderData.client_nom, telephone: orderData.client_telephone },
          modele: orderData.modele_nom || 'Sur mesure',
          mesures: orderData.mesures || {},
          tissuClient: Boolean(orderData.tissu_client),
          articles: orderData.articles || [],
          paiement: {
            ...commande.paiement,
            total: orderData.total,
            remise: orderData.remise,
            reste: orderData.reste,
          }
        });
        setRemiseValue(orderData.remise || 0);
      }
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de la remise', 'error');
    }
  };

  const handleAddPayment = async () => {
    if (!id || newPayment.montant <= 0) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.addCommandePaiement(token, id, { 
        montant: newPayment.montant, 
        mode_paiement: newPayment.mode 
      });
      showToast("Versement enregistré !");
      setShowPaymentModal(false);
      setNewPayment({ montant: 0, mode: 'Espèces' });
      
      // Refresh
      const [orderData, paymentsData] = await Promise.all([
        api.getCommande(token, id),
        api.getCommandePaiements(token, id)
      ]);
      
      if (orderData && commande) {
        setCommande({
          ...commande,
          ...orderData,
          paiement: {
            ...commande.paiement,
            acompte: orderData.acompte,
            reste: orderData.reste,
            historique: paymentsData
          }
        });
        setPaymentHistory(paymentsData);
      }
    } catch (err: any) {
      showToast(err.message || "Erreur paiement", "error");
    }
  };

  if (!user || loading || !commande) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 font-nunito text-gray-400">
        {loading ? <div className="flex flex-col items-center gap-2"><div className="w-8 h-8 border-4 border-foret-500 border-t-transparent rounded-full animate-spin" /><p>Chargement...</p></div> : 
        (!commande && id ? (
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
              <i className="ri-file-damage-line text-5xl" />
            </div>
            <p className="text-gray-500 font-semibold">Commande introuvable</p>
            <button onClick={() => navigate('/dashboard/atelier')} className="mt-3 text-foret-600 text-sm cursor-pointer underline">
              Retour à l&apos;atelier
            </button>
          </div>
        ) : null)}
      </div>
    );
  }

  const currentIdx = STATUS_ORDER.indexOf(commande.statut);
  const urgence = commande.dateLivraison ? new Date(commande.dateLivraison) <= new Date(Date.now() + 3 * 24 * 3600 * 1000) : false;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 md:px-6 py-4 flex items-center gap-2 md:gap-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer transition-colors text-gray-600"
              onClick={() => (window as any).toggleMobileMenu?.()}
              aria-label="Ouvrir le menu"
            >
              <i className="ri-menu-line text-xl" />
            </button>
            <button
              onClick={() => navigate(-1)}
              title="Retour"
              className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer transition-colors text-gray-500"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-playfair font-bold text-xl text-gray-900">{commande.id}</h2>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statutColors[commande.statut]}`}>
                {statutLabels[commande.statut]}
              </span>
              {urgence && commande.statut !== 'LIVRE' && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-300 flex items-center gap-1">
                  <i className="ri-alarm-warning-line" /> Urgent
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{commande.client?.nom} · Livraison le {commande.dateLivraison || 'Non définie'}</p>
          </div>
          <div className="flex items-center gap-2">
            {checkPermission(PERMISSIONS.DELETE_COMMANDE) && (
                !showDeleteConfirm ? (
                  <button onClick={() => setShowDeleteConfirm(true)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-100 cursor-pointer transition-colors" title="Supprimer">
                    <i className="ri-delete-bin-line" />
                  </button>
                ) : (
                  <div className="flex gap-1 animate-in fade-in slide-in-from-right-2 duration-300">
                    <button onClick={handleDelete} className="px-3 py-1.5 rounded-xl bg-red-500 text-white text-xs font-bold cursor-pointer">OUI</button>
                    <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-400 text-xs font-bold cursor-pointer transition-colors">NON</button>
                  </div>
                )
            )}
            {checkPermission(PERMISSIONS.CANCEL_COMMANDE) && commande.statut !== 'ANNULEE' && (
                !showCancelConfirm ? (
                  <button onClick={() => setShowCancelConfirm(true)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100 cursor-pointer transition-colors" title="Annuler la commande">
                    <i className="ri-close-circle-line" />
                  </button>
                ) : (
                  <div className="flex gap-1 items-center animate-in fade-in slide-in-from-right-2 duration-300">
                    <span className="text-[10px] text-red-500 font-bold">Annuler ?</span>
                    <button onClick={handleCancel} className="px-3 py-1.5 rounded-xl bg-amber-500 text-white text-xs font-bold cursor-pointer">OUI</button>
                    <button onClick={() => setShowCancelConfirm(false)} className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-400 text-xs font-bold cursor-pointer transition-colors">NON</button>
                  </div>
                )
            )}
            {checkPermission(PERMISSIONS.EDIT_COMMANDE) && (
                <button 
                   onClick={() => setShowEditModal(true)}
                   className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors"
                   title="Modifier"
                >
                  <i className="ri-edit-line text-lg" />
                </button>
            )}
            <button 
               onClick={() => setShowFacture(true)}
               className="w-9 h-9 flex items-center justify-center rounded-xl bg-orange-50 text-orange-600 hover:bg-orange-100 cursor-pointer transition-colors"
               title="Imprimer la facture"
            >
              <i className="ri-printer-line text-lg" />
            </button>
            {commande.statut !== 'LIVRE' && (() => {
              const smartAction = getSmartAction() as any;
              return (
                <button
                  onClick={() => smartAction.onClick ? smartAction.onClick() : setShowValidModal(true)}
                  disabled={smartAction.disabled || !(user.role === 'gerant' || user.role === 'superadmin' || user.role === 'responsable_atelier' || (user.role === 'coupeur' && commande.statut === 'EN_ATTENTE') || (user.role === 'tailleur' && commande.statut === 'EN_COUPE'))}
                  className={`flex items-center gap-2 ${smartAction.color || 'bg-foret-500'} hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-all whitespace-nowrap shadow-sm active:scale-95`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={smartAction.icon} />
                  </div>
                  {smartAction.label}
                </button>
              );
            })()}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto space-y-5">
            {/* Timeline */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-5 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-foret-600">
                  <i className="ri-git-branch-line" />
                </div>
                Suivi de production
              </h3>
              <CommandeTimeline currentStatut={commande.statut} validations={commande.validations} />
              {['EN_COUPE', 'EN_COUTURE'].includes(commande.statut) && (
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <p className="text-xs font-bold text-gray-400 mb-2.5 uppercase tracking-widest pl-1">Avancement détaillé des composants</p>
                  {renderDetailedSubTasks(commande, getActiveWorkshopType())}
                  {getActiveWorkshopType() === 'COUPE' && (commande.affectations || []).some((a: any) => a.type === 'BRODERIE') && (
                    <div className="mt-2.5">
                      <p className="text-[10px] font-bold text-gray-400 mb-1.5 uppercase tracking-widest pl-1">Broderie (Atelier Coupe)</p>
                      {renderDetailedSubTasks(commande, 'BRODERIE')}
                    </div>
                  )}
                </div>
              )}
              {commande.validations.length > 0 && (
                <div className="mt-5 border-t border-gray-50 pt-4">
                  <p className="text-xs font-bold text-gray-400 mb-3 uppercase tracking-widest">Historique des validations</p>
                  <div className="space-y-4">
                    {[...commande.validations].reverse().map((v, i) => (
                      <div key={i} className="flex gap-4 relative">
                        {/* Vertical line connector */}
                        {i < commande.validations.length - 1 && (
                          <div className="absolute left-[13px] top-7 bottom-[-16px] w-[2px] bg-gray-100" />
                        )}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${statutColors[v.etape].includes('bg-') ? statutColors[v.etape] : 'bg-gray-100'}`}>
                           <i className={`${nextStepIcon[v.etape] || 'ri-check-line'} text-xs ${statutColors[v.etape].includes('text-') ? statutColors[v.etape] : 'text-gray-600'}`} />
                        </div>
                        <div className="flex-1 pb-1">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-sm font-bold text-gray-800">{statutLabels[v.etape]}</span>
                            <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{v.date} à {v.heure}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                             <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-[8px] font-bold text-gray-600">
                               {v.validePar.charAt(0)}
                             </div>
                             <span className="font-semibold text-gray-600">{v.validePar}</span>
                             {v.commentaire && <span className="italic ml-2 text-gray-400 border-l border-gray-200 pl-2">"{v.commentaire}"</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Client + modèle */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center text-foret-600"><i className="ri-user-line" /></div>
                    Client & Modèle
                  </h3>
                  {checkPermission(PERMISSIONS.EDIT_COMMANDE) && (
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="text-xs text-foret-600 hover:text-foret-700 font-semibold cursor-pointer flex items-center gap-1"
                      title="Modifier"
                    >
                      <i className="ri-edit-line" />
                      Modifier
                    </button>
                  )}
                </div>
                <div className="flex gap-4">
                  <img src={api.getProxyUrl(commande.modelImage)} alt={commande.modele} className="w-24 h-24 rounded-xl object-cover object-top flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{commande.client?.nom}</p>
                    <a href={`tel:${commande.client?.telephone}`} className="text-sm text-foret-600 hover:underline flex items-center gap-1 mt-0.5">
                      <i className="ri-phone-line text-xs" /> {commande.client?.telephone}
                    </a>
                    <div className="mt-3 pt-3 border-t border-gray-50">
                      <p className="text-xs text-gray-500">Modèle</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">{commande.modele}</p>
                    </div>
                    {commande.notes && (
                      <div className="mt-2 bg-or-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-or-700 flex items-start gap-1">
                          <i className="ri-sticky-note-line flex-shrink-0 mt-0.5" />
                          {commande.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Détail des Articles (Multi-articles) */}
              {commande.articles && commande.articles.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6 lg:col-span-2 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 border-b border-gray-50 pb-3">
                    <div className="w-5 h-5 flex items-center justify-center text-foret-600"><i className="ri-list-check" /></div>
                    Détail des Articles & Coûts
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-gray-400 font-bold uppercase text-[10px] tracking-widest border-b border-gray-100">
                        <tr>
                          <th className="text-left py-3 px-2">Désignation</th>
                          <th className="text-center py-3 px-2">Qté</th>
                          <th className="text-right py-3 px-2">P.U.</th>
                          <th className="text-right py-3 px-2">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {commande.articles.map((art: any, idx: number) => {
                          const totalArt = Number(art.total_ligne || 0) + (art.tissus || []).reduce((acc: number, t: any) => acc + (Number(t.total_ligne) || 0), 0);
                          return (
                            <React.Fragment key={idx}>
                              <tr className="bg-gray-50/50">
                                <td className="py-3 px-2 font-bold text-gray-900">
                                  {art.type_habit_nom || art.nom || 'Article'} — {art.modele_nom || 'Sur mesure'}
                                </td>
                                <td className="py-3 px-2 text-center text-gray-500">{art.quantite || 1}</td>
                                <td className="py-3 px-2 text-right text-gray-500">—</td>
                                <td className="py-3 px-2 text-right font-black text-gray-900">{formatPrice(totalArt)}</td>
                              </tr>
                              {/* Ligne Couture */}
                              {Number(art.total_ligne) > 0 && (
                                <tr>
                                  <td className="py-2 px-2 pl-8 text-xs text-gray-500 flex items-center gap-2">
                                    <i className="ri-scissors-2-line text-blue-400" /> Couture (Main d'œuvre)
                                  </td>
                                  <td className="py-2 px-2 text-center text-xs text-gray-400">{art.quantite || 1}</td>
                                  <td className="py-2 px-2 text-right text-xs text-gray-400">{formatPrice(art.prix_unitaire || 0)}</td>
                                  <td className="py-2 px-2 text-right text-xs text-blue-600 font-bold">{formatPrice(art.total_ligne || 0)}</td>
                                </tr>
                              )}
                              {/* Lignes Tissus */}
                              {(art.tissus || []).map((t: any, tidx: number) => {
                                const canEditBoutique = user && ['superadmin', 'gerant', 'admin'].includes(user.role.toLowerCase());
                                return (
                                  <tr key={tidx}>
                                    <td className="py-2 px-2 pl-8 text-xs text-gray-500 flex items-center gap-2">
                                      <i className="ri-palette-line text-terre-400" /> {t.is_client_cloth ? 'Tissu client' : 'Tissu boutique'} : {t.tissu_nom || t.description || 'Tissu'}
                                      
                                      {!t.is_client_cloth && (
                                        canEditBoutique ? (
                                          <div className="inline-flex items-center gap-1 ml-2 bg-gray-100 p-0.5 rounded-md border border-gray-200">
                                            <button
                                              onClick={() => handleUpdateTissuBoutique(Number(t.id), 1)}
                                              disabled={Number(t.boutique_id) === 1 || !t.boutique_id}
                                              title="Passer en Boutique 1"
                                              className={`px-1.5 py-0.5 text-[8px] font-extrabold rounded transition-all cursor-pointer ${
                                                Number(t.boutique_id) !== 2
                                                  ? 'bg-terre-500 text-white shadow-xs'
                                                  : 'text-gray-400 hover:text-gray-700'
                                              }`}
                                            >
                                              B1
                                            </button>
                                            <button
                                              onClick={() => handleUpdateTissuBoutique(Number(t.id), 2)}
                                              disabled={Number(t.boutique_id) === 2}
                                              title="Passer en Boutique 2"
                                              className={`px-1.5 py-0.5 text-[8px] font-extrabold rounded transition-all cursor-pointer ${
                                                Number(t.boutique_id) === 2
                                                  ? 'bg-foret-500 text-white shadow-xs'
                                                  : 'text-gray-400 hover:text-gray-700'
                                              }`}
                                            >
                                              B2
                                            </button>
                                          </div>
                                        ) : (
                                          Number(t.boutique_id) === 2 ? (
                                            <span className="ml-2 px-1 py-0.5 text-[8px] font-extrabold rounded bg-foret-100 text-foret-700 border border-foret-200">B2</span>
                                          ) : (
                                            <span className="ml-2 px-1 py-0.5 text-[8px] font-extrabold rounded bg-terre-100 text-terre-700 border border-terre-200">B1</span>
                                          )
                                        )
                                      )}
                                    </td>
                                    <td className="py-2 px-2 text-center text-xs text-gray-400">{t.quantite}{t.tissu_unite || 'm'}</td>
                                    <td className="py-2 px-2 text-right text-xs text-gray-400">{formatPrice(t.prix_unitaire || 0)}</td>
                                    <td className="py-2 px-2 text-right text-xs text-terre-600 font-bold">{formatPrice(t.total_ligne || 0)}</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Mesures */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-4 print:hidden">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                       <div className="w-5 h-5 flex items-center justify-center text-foret-600"><i className="ri-ruler-line" /></div>
                       Mesures
                    </h3>
                    <div className="flex gap-2">
                       <button onClick={() => window.print()} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer" title="Imprimer la fiche">
                         <i className="ri-printer-line text-lg" />
                       </button>
                       <button onClick={exportAsImage} disabled={exporting} className="p-1.5 rounded-lg text-gray-400 hover:text-foret-600 hover:bg-foret-50 transition-all cursor-pointer" title="Exporter en Image">
                         <i className="ri-image-line text-lg" />
                       </button>
                       <button onClick={exportAsPDF} disabled={exporting} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all cursor-pointer" title="Exporter en PDF">
                         <i className="ri-file-pdf-line text-lg" />
                       </button>
                    </div>
                  </div>
                  <div ref={measuresRef} id="printable-measures" className="p-4 bg-white rounded-xl">
                    {/* Header for print/export */}
                    <div className="mb-6 pb-4 border-b border-gray-100 flex items-center justify-between">
                       <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-foret-500 rounded-lg flex items-center justify-center text-white font-black text-xl shadow-lg shadow-foret-100 overflow-hidden">
                             {branding.logoUrl ? (
                               <img src={api.getProxyUrl(branding.logoUrl)} alt="Logo" className="w-full h-full object-cover" />
                             ) : (
                               branding.nom.charAt(0)
                             )}
                          </div>
                          <div>
                             <h4 className="font-playfair font-black text-sm text-gray-900 uppercase tracking-tight leading-none">{branding.nom}</h4>
                             <p className="text-[10px] text-gray-400 font-bold mt-1 uppercase tracking-widest">Fiche de Mesures Client</p>
                          </div>
                       </div>
                       <div className="text-right">
                          <p className="text-sm font-black text-gray-900 uppercase">{commande.client?.nom}</p>
                          <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">{commande.id} · {new Date().toLocaleDateString('fr-FR')}</p>
                       </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                       {Object.entries(commande.mesures || {})
                         .filter(([k]) => k !== 'notes')
                         .map(([label, value]) => (
                         <div key={label} className="bg-foret-50 rounded-xl p-3 border border-foret-100/50">
                           <div className="flex items-center gap-1.5 mb-1">
                             <div className="w-3.5 h-3.5 flex items-center justify-center text-foret-500">
                               <i className="ri-pencil-ruler-2-line text-xs" />
                             </div>
                             <span className="text-xs text-foret-600 font-bold capitalize">{label}</span>
                           </div>
                           <p className="text-xl font-bold text-foret-800">{String(value) || '—'}</p>
                           <p className="text-xs text-foret-500">cm</p>
                         </div>
                       ))}
                       {Object.keys(commande.mesures || {}).filter(k => k !== 'notes').length === 0 && (
                          <p className="text-xs text-gray-400 italic col-span-2 lg:col-span-3">Aucune mesure enregistrée pour cette commande.</p>
                       )}
                    </div>
                    {commande.mesures?.notes && (
                      <div className="mt-3 bg-gray-50 rounded-xl p-3 border-l-2 border-foret-300">
                        <p className="text-xs text-gray-500 font-semibold mb-1 uppercase tracking-tight uppercase tracking-widest">Notes techniques</p>
                        <p className="text-sm text-gray-700 leading-relaxed">{commande.mesures.notes}</p>
                      </div>
                    )}
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                       <p className="text-[9px] font-black text-gray-300 tracking-widest uppercase italic">L'élégance sur-mesure · Bamako</p>
                       <p className="text-[9px] text-gray-300 font-bold">Document généré le {new Date().toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
              </div>

              {/* Tissu */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center text-foret-600"><i className="ri-palette-line" /></div>
                    Tissu
                  </h3>
                  {!commande.tissuClient && checkPermission(PERMISSIONS.EDIT_COMMANDE) && (
                    <button
                      onClick={() => setShowTissuModal(true)}
                      className="text-xs text-foret-600 hover:text-foret-700 font-semibold cursor-pointer flex items-center gap-1 whitespace-nowrap"
                      title="Modifier le tissu"
                    >
                      <i className="ri-edit-line" />
                      {commande.tissuStockId ? 'Modifier' : 'Assigner'}
                    </button>
                  )}
                </div>

                <div className={`rounded-xl p-4 ${commande.tissuClient ? 'bg-blue-50 border border-blue-100' : commande.tissuStockId ? 'bg-foret-50 border border-foret-100' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-3">
                    {commande.tissuImage ? (
                      <img src={api.getProxyUrl(commande.tissuImage) || 'https://images.unsplash.com/photo-1544441893-675973e31985?w=100&q=80'} alt="Tissu" className="w-14 h-14 rounded-xl object-cover object-top flex-shrink-0" />
                    ) : (
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${commande.tissuClient ? 'bg-blue-200' : commande.tissuStockId ? 'bg-foret-200 text-foret-600' : 'bg-red-200 text-red-600'}`}>
                        <i className={`text-2xl ${commande.tissuClient ? 'ri-user-heart-line text-blue-600' : commande.tissuStockId ? 'ri-palette-line' : 'ri-alert-line'}`} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${commande.tissuClient ? 'bg-blue-200 text-blue-800' : 'bg-foret-200 text-foret-800'}`}>
                          {commande.tissuClient ? 'Tissu client' : 'Stock boutique'}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{commande.tissuNom || commande.tissuDescription}</p>
                      {commande.quantiteTissu && (
                        <p className="text-xs text-terre-600 font-bold mt-0.5"><i className="ri-ruler-line" /> {commande.quantiteTissu} mètres</p>
                      )}
                      {!commande.tissuClient && !commande.tissuStockId && (
                        <p className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1">
                          <i className="ri-alert-line" />
                          Tissu non encore assigné
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Affectation équipe (Granulaire) */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm overflow-hidden flex flex-col">
                <div className="flex items-center justify-between mb-5">
                   <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                     <div className="w-5 h-5 flex items-center justify-center text-foret-600"><i className="ri-team-line" /></div>
                     Équipe & Tâches
                   </h3>
                   {checkPermission(PERMISSIONS.EDIT_COMMANDE) && (
                      <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
                        <button onClick={() => setShowAssignModal('COUPE')} className="text-[10px] font-black bg-blue-50 text-blue-600 px-2.5 py-1.5 rounded-xl hover:bg-blue-100 transition-all cursor-pointer uppercase tracking-tight whitespace-nowrap">
                          + Coupe
                        </button>
                        <button onClick={() => setShowAssignModal('BRODERIE')} className="text-[10px] font-black bg-amber-50 text-amber-600 px-2.5 py-1.5 rounded-xl hover:bg-amber-100 transition-all cursor-pointer uppercase tracking-tight whitespace-nowrap">
                          + Broderie
                        </button>
                        <button onClick={() => setShowAssignModal('COUTURE')} className="text-[10px] font-black bg-purple-50 text-purple-600 px-2.5 py-1.5 rounded-xl hover:bg-purple-100 transition-all cursor-pointer uppercase tracking-tight whitespace-nowrap">
                          + Couture
                        </button>
                      </div>
                   )}
                </div>

                <div className="space-y-4">
                  {((commande as any).affectations || []).length === 0 && (
                    <div className="text-center py-6 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                       <i className="ri-user-add-line text-2xl text-gray-300 mb-1 block" />
                       <p className="text-xs text-gray-400 font-medium">Aucun personnel assigné pour le moment.</p>
                    </div>
                  )}

                  {((commande as any).affectations || []).map((aff: any) => (
                    <div key={aff.id} className={`relative flex flex-col sm:flex-row sm:items-center justify-between rounded-2xl p-4 border transition-all ${
                      aff.type === 'COUPE' ? 'bg-blue-50 border-blue-100' : 
                      aff.type === 'COUTURE' ? 'bg-purple-50 border-purple-100' : 'bg-amber-50 border-amber-100'
                    }`}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                         <div className={`w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 shadow-sm font-black text-white ${
                            aff.type === 'COUPE' ? 'bg-blue-500' : 
                            aff.type === 'COUTURE' ? 'bg-purple-500' : 'bg-amber-500'
                         }`}>
                           {aff.personnel_nom.charAt(0)}
                         </div>
                         <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                               <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg ${
                                 aff.type === 'COUPE' ? 'bg-blue-100 text-blue-700' :
                                 aff.type === 'COUTURE' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
                               }`}>
                                 {aff.type}
                               </span>
                               {aff.statut === 'TERMINE' ? (
                                  <span className="text-[10px] text-green-600 font-bold flex items-center gap-1 bg-green-100 px-2 py-0.5 rounded-lg border border-green-200">
                                    <i className="ri-checkbox-circle-fill" /> Terminé
                                  </span>
                               ) : (
                                  <span className="text-[10px] text-gray-500 font-bold flex items-center gap-1 bg-white/50 px-2 py-0.5 rounded-lg border border-white">
                                    <i className={aff.statut === 'EN_COURS' ? 'ri-loader-4-line animate-spin' : 'ri-time-line'} /> {aff.statut.replace('_', ' ')}
                                  </span>
                               )}
                            </div>
                            <p className="text-sm font-black text-gray-900 truncate">{aff.personnel_nom}</p>
                            <p className="text-[11px] text-gray-500 font-medium leading-tight mt-0.5">
                              <span className="text-gray-400 font-bold">Article :</span> <span className="text-gray-800 font-bold">{getArticleNomHelper(aff, commande.articles, commande.modele)}</span>
                              {aff.quantite && <span className="ml-1.5 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded text-[10px] font-black text-gray-700">Qté: {aff.quantite}</span>}
                            </p>
                            <p className="text-[11px] text-gray-500 font-medium leading-tight mt-0.5">
                              <span className="text-gray-400 font-bold">Tâches :</span> <span className="text-gray-700">{aff.tache}</span>
                            </p>
                         </div>
                      </div>

                      <div className="mt-3 sm:mt-0 sm:ml-4 flex items-center justify-between sm:justify-end gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 border-white/50">
                         <div className="text-right">
                            <p className={`text-[10px] font-black uppercase tracking-tighter ${
                              aff.type === 'COUPE' ? 'text-blue-400' :
                              aff.type === 'COUTURE' ? 'text-purple-400' : 'text-amber-500'
                            }`}>Coût</p>
                            <p className="text-sm font-black text-gray-900">
                             {Number(aff.cout_main_doeuvre || 0).toLocaleString()} <span className="text-[10px] font-bold">F</span>
                            </p>
                         </div>

                         <div className="flex gap-1">
                            {aff.statut !== 'TERMINE' && (
                               <button 
                                 onClick={() => {
                                   if (aff.statut === 'EN_ATTENTE') {
                                     if (Number(aff.quantite || 1) > 1) {
                                       setSingleAffToStart(aff);
                                       setShowStartModal(true);
                                     } else {
                                       handleUpdateAffectationStatus(aff.id, 'EN_COURS');
                                     }
                                   } else {
                                     setSingleAffToValidate(aff);
                                     setSelectedAffIds([Number(aff.id)]);
                                     setShowValidModal(true);
                                   }
                                 }}
                                 className="w-8 h-8 flex items-center justify-center rounded-xl bg-white text-gray-700 hover:bg-gray-100 transition-all cursor-pointer shadow-sm"
                                 title={aff.statut === 'EN_ATTENTE' ? "Démarrer" : "Valider la tâche"}
                               >
                                 <i className={aff.statut === 'EN_ATTENTE' ? 'ri-play-fill' : 'ri-check-line'} />
                               </button>
                            )}
                            {checkPermission(PERMISSIONS.EDIT_COMMANDE) && (
                              <div className="flex gap-1">
                                <button 
                                   onClick={() => setAssignmentToEdit(aff)}
                                   className="w-8 h-8 flex items-center justify-center rounded-xl bg-white text-gray-700 hover:bg-gray-100 transition-all cursor-pointer shadow-sm"
                                   title="Modifier l'assignation"
                                >
                                  <i className="ri-pencil-line text-sm" />
                                </button>
                                <button 
                                   onClick={() => handleDeleteAffectation(aff.id)}
                                   className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/50 text-red-400 hover:bg-red-50 hover:text-red-500 transition-all cursor-pointer"
                                   title="Retirer"
                                >
                                  <i className="ri-delete-bin-line text-sm" />
                                </button>
                              </div>
                            )}
                         </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Paiement */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center text-foret-600"><i className="ri-money-franc-circle-line" /></div>
                    Paiement & Versements
                    
                    <span className="text-[10px] text-gray-400 font-bold ml-3 mr-1">Boutique :</span>
                    {user && ['superadmin', 'gerant', 'admin'].includes(user.role.toLowerCase()) ? (
                      <div className="inline-flex items-center gap-1 bg-gray-100 p-0.5 rounded-md border border-gray-200">
                        <button
                          onClick={() => handleUpdateCommandeBoutique(1)}
                          disabled={Number(commande?.boutique_id) === 1}
                          title="Affecter à la Boutique 1"
                          className={`px-1.5 py-0.5 text-[8px] font-extrabold rounded transition-all cursor-pointer ${
                            Number(commande?.boutique_id) === 1 || !commande?.boutique_id
                              ? 'bg-terre-500 text-white shadow-xs'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          B1
                        </button>
                        <button
                          onClick={() => handleUpdateCommandeBoutique(2)}
                          disabled={Number(commande?.boutique_id) === 2}
                          title="Affecter à la Boutique 2"
                          className={`px-1.5 py-0.5 text-[8px] font-extrabold rounded transition-all cursor-pointer ${
                            Number(commande?.boutique_id) === 2
                              ? 'bg-foret-500 text-white shadow-xs'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          B2
                        </button>
                      </div>
                    ) : (
                      <span className={`px-1 py-0.5 text-[8px] font-extrabold rounded border ${
                        Number(commande?.boutique_id) === 2 
                          ? 'bg-foret-100 text-foret-700 border-foret-200' 
                          : 'bg-terre-100 text-terre-700 border-terre-200'
                      }`}>
                        {Number(commande?.boutique_id) === 2 ? 'B2' : 'B1'}
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center gap-2">
                    {checkPermission(PERMISSIONS.EDIT_COMMANDE) && (
                      <button
                        onClick={() => { setRemiseValue(commande.paiement.remise || 0); setShowRemiseModal(true); }}
                        title="Appliquer ou modifier une remise"
                        className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5"
                      >
                        <i className="ri-price-tag-3-line" />
                        {commande.paiement.remise > 0 ? `Remise : ${formatPrice(commande.paiement.remise)}` : 'Appliquer remise'}
                      </button>
                    )}
                    {commande.paiement.reste > 0 && (
                      <button
                        onClick={() => setShowPaymentModal(true)}
                        className="px-3 py-1.5 bg-foret-500 hover:bg-foret-600 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1.5"
                      >
                        <i className="ri-add-circle-line" />
                        Nouveau versement
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Total commande</p>
                    <p className="text-xl md:text-2xl font-black text-gray-900 leading-tight">{formatPrice(commande.paiement.total, false)}</p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">FCFA</p>
                    {commande.paiement.remise > 0 && (
                      <p className="text-[10px] text-amber-600 font-bold mt-1"><i className="ri-price-tag-3-line" /> Remise : -{formatPrice(commande.paiement.remise, false)} FCFA</p>
                    )}
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                    <p className="text-xs text-green-600 mb-1">Total versé</p>
                    <p className="text-xl md:text-2xl font-black text-green-700 leading-tight">{formatPrice(commande.paiement.acompte, false)}</p>
                    <p className="text-[10px] text-green-500 font-bold uppercase tracking-widest mt-1">FCFA</p>
                  </div>
                  <div className={`rounded-xl p-4 text-center border ${commande.paiement.reste > 0 ? 'bg-orange-50 border-orange-100' : 'bg-green-50 border-green-100 shadow-sm shadow-green-50'}`}>
                    <p className={`text-xs mb-1 ${commande.paiement.reste > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {commande.paiement.reste > 0 ? 'Reste à payer' : 'Soldé'}
                    </p>
                    <p className={`text-xl md:text-2xl font-black leading-tight ${commande.paiement.reste > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                      {formatPrice(commande.paiement.reste, false)}
                    </p>
                    <p className={`text-[10px] font-bold uppercase tracking-widest mt-1 ${commande.paiement.reste > 0 ? 'text-orange-400' : 'text-green-400'}`}>FCFA</p>
                  </div>
                </div>

                {/* Paiement History */}
                {paymentHistory.length > 0 && (
                  <div className="mt-6 border-t border-gray-50 pt-4">
                    <p className="text-[10px] font-bold text-gray-400 mb-3 uppercase tracking-widest">Historique des versements</p>
                    <div className="space-y-2">
                      {paymentHistory.map((p, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                              <i className="ri-check-line text-sm" />
                            </div>
                            <div>
                               <p className="text-sm font-bold text-gray-800">{formatPrice(p.montant)}</p>
                               <p className="text-[10px] text-gray-400">{new Date(p.created_at).toLocaleDateString('fr-FR')} à {new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} · {p.mode_paiement}</p>
                            </div>
                          </div>
                          <span className="text-[9px] px-2 py-0.5 rounded bg-white text-gray-400 font-bold border border-gray-100">Encaissé</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3">
                  <style>
                    {`
                      .payment-progress-bar { width: ${Math.round((commande.paiement.acompte / commande.paiement.total) * 100)}%; }
                    `}
                  </style>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-400 rounded-full transition-all payment-progress-bar"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {Math.round((commande.paiement.acompte / commande.paiement.total) * 100)}% payé
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Validation Modal */}
      {showValidModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowValidModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair font-bold text-lg text-gray-900 mb-1">
              {singleAffToValidate ? "Valider la tâche" : "Confirmer l'action"}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {singleAffToValidate ? (
                <>
                  Souhaitez-vous valider la tâche de <strong>{singleAffToValidate.personnel_nom}</strong> ({singleAffToValidate.type}) ?
                </>
              ) : (
                <>
                  Souhaitez-vous effectuer l&apos;action : <strong>{getSmartAction().label}</strong> pour la commande <strong>{commande.id}</strong> ?
                </>
              )}
            </p>

            {/* Liste récapitulative des affectations à valider */}
            {(() => {
              const activeType = getActiveWorkshopType();
              const cmdAffs = singleAffToValidate 
                ? [singleAffToValidate] 
                : (commande.affectations || []).filter(
                    (a: any) => a.type === activeType || (activeType === 'COUPE' && a.type === 'BRODERIE')
                  );
              
              if (cmdAffs.length === 0) return null;

              return (
                <div className="space-y-3 max-h-[300px] overflow-y-auto mb-6 p-3 bg-gray-50 rounded-2xl border border-gray-100 custom-scrollbar">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1 mb-2">
                    {singleAffToValidate ? "Détails de la tâche" : "Tâches à valider"}
                  </p>
                  {cmdAffs.map((aff: any) => {
                    const isCompleted = aff.statut === 'TERMINE';
                    const isChecked = selectedAffIds.includes(aff.id) || isCompleted;
                    
                    return (
                      <div 
                        key={aff.id} 
                        className={`flex items-start gap-3 p-3.5 rounded-2xl border transition-all ${
                          !singleAffToValidate ? 'cursor-pointer' : ''
                        } ${
                          isChecked 
                            ? 'bg-foret-50/20 border-foret-200' 
                            : 'bg-white border-gray-100 hover:border-gray-200'
                        }`}
                        onClick={() => {
                          if (singleAffToValidate || isCompleted) return;
                          setSelectedAffIds(prev => prev.includes(aff.id) ? prev.filter(id => id !== aff.id) : [...prev, aff.id]);
                        }}
                      >
                        {!singleAffToValidate && (
                          <input 
                            type="checkbox"
                            className="w-5 h-5 rounded border-gray-300 text-foret-600 focus:ring-foret-500 mt-0.5 cursor-pointer"
                            checked={isChecked}
                            disabled={isCompleted}
                            readOnly
                          />
                        )}
                        <div className="flex-1 min-w-0 flex flex-col gap-1">
                          <div className="flex items-center justify-between gap-1.5 flex-wrap">
                            <span className="text-xs font-black text-gray-800">
                              <span className="text-gray-400 font-bold">Exécutant :</span> {aff.personnel_nom}
                            </span>
                            {isCompleted ? (
                              <span className="text-[8px] bg-green-100 text-green-700 px-1.5 py-0.2 rounded-md font-black border border-green-200 uppercase tracking-wider">Terminé</span>
                            ) : isChecked ? (
                              <span className="text-[8px] bg-foret-100 text-foret-700 px-1.5 py-0.2 rounded-md font-black border border-foret-200 uppercase tracking-wider">À valider</span>
                            ) : (
                              <span className="text-[8px] bg-amber-50 text-amber-700 px-1.5 py-0.2 rounded-md font-black border border-amber-200 uppercase tracking-wider">En attente</span>
                            )}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1 mt-1 text-[11px]">
                            <p className="text-gray-600">
                              <span className="text-gray-400 font-bold">Tâche :</span> <span className="font-semibold text-gray-700">{aff.tache}</span>
                            </p>
                            <p className="text-gray-600">
                              <span className="text-gray-400 font-bold">Couture :</span> <span className="font-bold text-gray-700">{getArticleNomHelper(aff, commande.articles, commande.modele)}</span>
                            </p>
                            <p className="text-gray-600 flex items-center gap-2 flex-wrap">
                              <span className="text-gray-400 font-bold">Quantité :</span>
                              {!isCompleted && isChecked && Number(aff.quantite) > 1 ? (
                                <select
                                  value={completedQuantities[Number(aff.id)] ?? Number(aff.quantite || 1)}
                                  onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setCompletedQuantities(prev => ({ ...prev, [aff.id]: val }));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="border border-gray-200 rounded-lg px-2 py-0.5 text-xs bg-white font-bold focus:outline-none focus:ring-2 focus:ring-foret-400 cursor-pointer shadow-sm"
                                >
                                  {Array.from({ length: Number(aff.quantite) }, (_, i) => i + 1).map(val => (
                                    <option key={val} value={val}>{val} / {aff.quantite} pièce{val > 1 ? 's' : ''}</option>
                                  ))}
                                </select>
                              ) : (
                                <span className="font-black text-gray-800">{aff.quantite || 1} pièces</span>
                              )}
                            </p>
                          </div>

                          {isChecked && (
                            <div className="mt-1.5 flex items-center gap-1.5 text-[9px] font-black text-foret-600 bg-white border border-foret-100 px-2 py-0.5 rounded-lg self-start shadow-sm">
                              <i className="ri-shield-user-line text-xs" />
                              <span>Validateur : {user?.nom || 'Responsable'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Commentaire (optionnel)</label>
            <textarea
              value={validComment}
              onChange={e => setValidComment(e.target.value)}
              rows={3}
              maxLength={200}
              placeholder="Observation, remarque sur la pièce…"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 resize-none mb-4"
            />
            <div className="flex gap-3">
              <button onClick={() => setShowValidModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                Annuler
              </button>
              <button onClick={handleAvancer} className="flex-1 py-2.5 rounded-xl bg-foret-500 text-white font-semibold text-sm cursor-pointer hover:bg-foret-600 whitespace-nowrap">
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start Task Modal */}
      {showStartModal && singleAffToStart && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowStartModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                <i className="ri-play-circle-line text-2xl animate-pulse" />
              </div>
              <div>
                <h3 className="font-playfair font-bold text-lg text-gray-900 leading-tight">Démarrer la tâche</h3>
                <p className="text-xs text-gray-400">Commande {commande.id} · {commande.client?.nom}</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-2.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1 text-xs">
                  <p className="text-gray-600">
                    <span className="text-gray-400 font-bold">Exécutant :</span> <span className="font-bold text-gray-800">{singleAffToStart.personnel_nom}</span>
                  </p>
                  <p className="text-gray-600">
                    <span className="text-gray-400 font-bold">Atelier :</span> <span className="font-bold text-gray-800">{singleAffToStart.type}</span>
                  </p>
                  <p className="text-gray-600">
                    <span className="text-gray-400 font-bold">Couture :</span> <span className="font-bold text-gray-800">{getArticleNomHelper(singleAffToStart, commande.articles, commande.modele)}</span>
                  </p>
                  <p className="text-gray-600">
                    <span className="text-gray-400 font-bold">Tâche :</span> <span className="font-semibold text-gray-700">{singleAffToStart.tache}</span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-widest">
                  Nombre de pièces à démarrer
                </label>
                <select
                  value={startQuantity}
                  onChange={(e) => setStartQuantity(Number(e.target.value))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer shadow-sm"
                >
                  {Array.from({ length: Number(singleAffToStart.quantite) }, (_, i) => i + 1).map(val => (
                    <option key={val} value={val}>{val} / {singleAffToStart.quantite} pièce{val > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              {startQuantity < Number(singleAffToStart.quantite) && (
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-[11px] text-blue-700 flex items-start gap-2">
                  <i className="ri-information-line text-sm flex-shrink-0 mt-0.5 animate-bounce" />
                  <p>
                    <strong>Note :</strong> La tâche sera scindée. <strong>{startQuantity}</strong> pièce{startQuantity > 1 ? 's passeront' : ' passera'} en cours, tandis que <strong>{Number(singleAffToStart.quantite) - startQuantity}</strong> restera en attente.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowStartModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                Annuler
              </button>
              <button onClick={handleStartTask} className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm cursor-pointer hover:bg-blue-700 whitespace-nowrap">
                Démarrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remise Modal */}
      {showRemiseModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRemiseModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <i className="ri-price-tag-3-line text-amber-600 text-lg" />
              </div>
              <div>
                <h3 className="font-playfair font-bold text-base text-gray-900">Appliquer une remise</h3>
                <p className="text-xs text-gray-400">Commande {commande.id} · {commande.client?.nom}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>Montant brut</span>
                <span className="font-bold text-gray-800">{formatPrice(commande.paiement.total + (commande.paiement.remise || 0))}</span>
              </div>
              <div className="flex justify-between text-amber-600">
                <span>Remise appliquée</span>
                <span className="font-bold">- {formatPrice(remiseValue)}</span>
              </div>
              <div className="flex justify-between text-foret-700 border-t border-gray-200 pt-1.5">
                <span className="font-bold">Net à payer</span>
                <span className="font-black">{formatPrice(Math.max(0, (commande.paiement.total + (commande.paiement.remise || 0)) - remiseValue))}</span>
              </div>
            </div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-widest">
              Montant de la remise (FCFA)
            </label>
            <input
              type="number"
              min={0}
              max={commande.paiement.total + (commande.paiement.remise || 0)}
              value={remiseValue}
              onChange={e => setRemiseValue(Math.max(0, Number(e.target.value)))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-amber-400 mb-4"
              placeholder="Ex: 5000"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShowRemiseModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm cursor-pointer hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleApplyRemise}
                className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold text-sm cursor-pointer transition-colors"
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tissu Assign Modal */}
      {showTissuModal && (
        <TissuAssignModal
          currentTissuStockId={commande.tissuStockId}
          quantiteRequise={commande.quantiteTissu ?? 3}
          onAssign={handleTissuAssign}
          onClose={() => setShowTissuModal(false)}
        />
      )}

      {/* Edit Order Modal */}
      {showEditModal && editForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
               <h3 className="font-playfair font-bold text-xl text-gray-900">Modifier la commande</h3>
               <button onClick={() => setShowEditModal(false)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 text-gray-400 cursor-pointer hover:bg-gray-200 transition-colors" title="Fermer">
                 <i className="ri-close-line" />
               </button>
            </div>
            
            <div className="space-y-4">
               <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">Nom du Client</label>
                    <input 
                      type="text" 
                      value={editForm.client_nom || ''} 
                      onChange={e => setEditForm({...editForm, client_nom: e.target.value})} 
                      disabled={!checkPermission(PERMISSIONS.EDIT_CLIENT)}
                      placeholder="Nom du client"
                      title="Nom du client"
                      className={`w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-foret-400 focus:border-transparent outline-none transition-all ${!checkPermission(PERMISSIONS.EDIT_CLIENT) ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`} 
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-client-tel" className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">Téléphone</label>
                    <input 
                      id="edit-client-tel"
                      type="text" 
                      value={editForm.client_tel || ''} 
                      onChange={e => setEditForm({...editForm, client_tel: e.target.value})} 
                      disabled={!checkPermission(PERMISSIONS.EDIT_CLIENT)}
                      placeholder="Téléphone"
                      title="Téléphone du client"
                      className={`w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-foret-400 focus:border-transparent outline-none transition-all ${!checkPermission(PERMISSIONS.EDIT_CLIENT) ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`} 
                    />
                  </div>
               </div>
               
               <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="edit-total" className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">Prix Total (FCFA)</label>
                    <input id="edit-total" type="number" value={editForm.total} onChange={e => setEditForm({...editForm, total: e.target.value})} placeholder="0" title="Prix total" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-foret-400 focus:border-transparent outline-none transition-all" />
                  </div>
                  <div>
                    <label htmlFor="edit-acompte" className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">Acompte (FCFA)</label>
                    <input id="edit-acompte" type="number" value={editForm.acompte} onChange={e => setEditForm({...editForm, acompte: e.target.value})} placeholder="0" title="Acompte versé" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-foret-400 focus:border-transparent outline-none transition-all" />
                  </div>
               </div>

               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label htmlFor="edit-mode-paiement" className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">Mode de paiement</label>
                   <select 
                     id="edit-mode-paiement"
                     title="Mode de paiement"
                     value={editForm.mode_paiement} 
                     onChange={e => setEditForm({...editForm, mode_paiement: e.target.value})} 
                     className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-foret-400 outline-none"
                   >
                      <option value="Espèces">Espèces</option>
                      <option value="Orange Money">Orange Money</option>
                      <option value="Moov Money">Moov Money</option>
                      <option value="Virement">Virement</option>
                   </select>
                 </div>
                 <div>
                   <label htmlFor="edit-date-livraison" className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">Livraison prévue</label>
                   <input 
                     id="edit-date-livraison"
                     title="Date de livraison"
                     type="date" 
                     placeholder="Date"
                     value={editForm.date_livraison || ''} 
                     onChange={e => setEditForm({...editForm, date_livraison: e.target.value})} 
                     className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-foret-400 focus:border-transparent outline-none transition-all" 
                   />
                 </div>
               </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider text-foret-600">Modèle catalogue (optionnel)</label>
                  <button 
                    onClick={() => setShowModeleModal(true)}
                    className="w-full flex items-center gap-4 p-3 border-2 border-dashed border-gray-200 rounded-2xl hover:border-foret-400 hover:bg-foret-50 transition-all text-left group"
                    title="Cliquer pour changer de modèle"
                  >
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 shadow-sm">
                      {editForm.modele_id ? (
                        <img 
                          src={api.getProxyUrl(modeles.find(m => String(m.id) === String(editForm.modele_id))?.image_url)} 
                          alt="Modèle" 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <i className="ri-image-add-line text-xl" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {editForm.modele_id ? modeles.find(m => String(m.id) === String(editForm.modele_id))?.nom : 'Sur mesure (aucun modèle choisi)'}
                      </p>
                      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest mt-0.5 group-hover:text-foret-500 transition-colors">
                        Cliquer pour modifier le modèle
                      </p>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 text-xl group-hover:text-foret-400" />
                  </button>
                </div>

                <div>
                 <label htmlFor="edit-notes" className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">Notes / Instructions spéciales</label>
                 <textarea id="edit-notes" rows={3} value={editForm.notes || ''} onChange={e => setEditForm({...editForm, notes: e.target.value})} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-foret-400 focus:border-transparent outline-none transition-all resize-none" placeholder="Notes additionnelles..." />
               </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button onClick={() => setShowEditModal(false)} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm cursor-pointer hover:bg-gray-50 transition-colors">Annuler</button>
              <button onClick={handleUpdate} className="flex-1 py-3 rounded-xl bg-foret-500 text-white font-bold text-sm cursor-pointer hover:bg-foret-600 transition-all shadow-lg shadow-foret-500/20">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
      {showModeleModal && (
        <ModeleSelectModal 
          currentModeleId={editForm.modele_id}
          onSelect={(m) => {
            setEditForm({
              ...editForm, 
              modele_id: m.id,
              total: m.prix // Synchronize price automatically
            });
            setShowModeleModal(false);
          }}
          onClose={() => setShowModeleModal(false)}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="mb-4">
              <h3 className="font-playfair font-black text-xl text-gray-900 border-b border-gray-100 pb-2">Nouveau versement</h3>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-2 flex items-center gap-1.5">
                <i className="ri-information-line text-foret-500" />
                Reste à payer : <span className="text-orange-600">{formatPrice(commande.paiement.reste)}</span>
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Montant du versement (FCFA)</label>
                <div className="relative">
                   <i className="ri-money-franc-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                   <input
                    type="number"
                    value={newPayment.montant || ''}
                    onChange={e => setNewPayment({ ...newPayment, montant: parseFloat(e.target.value) || 0 })}
                    placeholder="Ex: 5000"
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-lg font-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-foret-400 placeholder:text-gray-300"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Mode de paiement</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Espèces', 'Mobile Money'].map(m => (
                    <button
                      key={m}
                      onClick={() => setNewPayment({ ...newPayment, mode: m })}
                      className={`py-3 rounded-xl text-xs font-bold border transition-all ${newPayment.mode === m ? 'bg-foret-500 text-white border-foret-500 shadow-md' : 'bg-white text-gray-500 border-gray-100 hover:border-gray-300'}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setShowPaymentModal(false)} className="flex-1 py-4 rounded-2xl bg-gray-100 text-gray-500 font-black text-xs uppercase tracking-widest hover:bg-gray-200 transition-colors">
                Annuler
              </button>
              <button 
                onClick={handleAddPayment} 
                disabled={newPayment.montant <= 0 || newPayment.montant > commande.paiement.reste}
                className="flex-1 py-4 rounded-2xl bg-foret-500 text-white font-black text-xs uppercase tracking-widest hover:bg-foret-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-foret-500/20"
              >
                Encaisser
              </button>
            </div>
          </div>
        </div>
      )}

      {showFacture && (
        <FactureModal 
          entreprise={branding} 
          factureData={{ type: 'commande', data: commande }} 
          onClose={() => setShowFacture(false)} 
        />
      )}
      {(showAssignModal || assignmentToEdit) && (
        <AssignmentModal 
          commandeIds={[Number(id)]}
          type={assignmentToEdit ? assignmentToEdit.type : showAssignModal!}
          modeleTypeId={commande?.modele_id}
          assignmentToEdit={assignmentToEdit}
          onClose={() => {
            setShowAssignModal(null);
            setAssignmentToEdit(null);
          }}
          onSuccess={async () => {
             const token = localStorage.getItem('atelier_bassoum_token');
             if (token && id) {
                const data = await api.getCommande(token, id);
                if (data) setCommande(prev => prev ? { ...prev, affectations: data.affectations || [], paiement: { ...prev.paiement, reste: data.reste } } : null);
                showToast(assignmentToEdit ? "Assignation modifiée avec succès !" : "Personnel assigné avec succès !");
             }
          }}
        />
      )}
    </div>
  );
}